import {Button, Input, message, Radio, Select, Tabs} from 'antd'
import React, {useMemo, useState} from 'react'
import {ElementShapeType, Project, TypedElement} from '../../../entities/project'
import {ToolType} from '../toolLayers/CanvasToolManager'
import {instance as projectService} from '../../../services/ProjectsService'
import {instance as elementTypeService} from '../../../services/ElementTypeService'
import {ElementTypeId} from '../../../entities/elementType'
import {TransformerUIManager} from '../transformerUis/TransformerUIManager'
import {useRevalidator, useSubmit} from 'react-router-dom'
import {useIntl} from 'react-intl'
import './EditorCreateElements.less'
import {
  BuildOutlined,
  CloseOutlined,
  FullscreenOutlined,
  GatewayOutlined,
  Loading3QuartersOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {TemplatesGallery} from './TemplatesGallery'

export type ModeType = 'create' | 'edit' | 'delete' | null

export type EditorCreateElementsProps = {
  project: Project
  selectedElementType: ElementTypeId
  onElementTypeSelected: (elementTypeId: ElementTypeId) => void
  availableElementTypes: ElementTypeId[]
  currentElementName: string
  onCurrentElementNameChange: (newName: string) => void
  tool?: ToolType
  onToolSelected: (tool: ToolType) => void
  currentElement: TypedElement
  onCurrentElementSelected: (element: TypedElement) => void
  onCurrentElementDeleted: (deletedElementId: number) => void
  currentMode: ModeType
  onCurrentModeChange: (currentMode: ModeType) => void
  elementsList: TypedElement[]
  onCurrentElementUpdated: (updatedElement: TypedElement) => void
  isUnsaved: boolean
  transformerProps?: any
  onTransformerPropsChange?: (newProps: any) => void
  onDraggableElementChange: (name: string) => void
  onSave: () => void
}

export function EditorCreateElements(props: EditorCreateElementsProps) {
  const [messageApi, contextHolder] = message.useMessage()
  const submit = useSubmit()
  const intl = useIntl()

  const availableShapeTypesSet = useMemo(
    () => new Set(elementTypeService.getElementType(props.selectedElementType).availableShapeTypes),
    [props.selectedElementType]
  )

  const templates = elementTypeService.getElementTypeTemplates(props.selectedElementType) || {}

  const shapes = [
    {value: 'rect', icon: <GatewayOutlined />},
    {value: 'polygon', icon: <BuildOutlined />},
    {value: 'ellipse', icon: <Loading3QuartersOutlined />},
  ] as {value: ElementShapeType; icon: React.ReactElement}[]

  const showTemplates = Object.keys(templates).length > 0
  const showShapes = shapes.some((shape) => availableShapeTypesSet.has(shape.value))

  return (
    <div className="editor-create-elements">
      <div className="editor-fields-name">{intl.formatMessage({id: 'chooseMode'})}:</div>
      <Radio.Group
        value={props.currentMode}
        onChange={(e) => props.onCurrentModeChange(e.target.value)}
        className="editor-fields-radio-group"
      >
        <Radio.Button className="editor-fields-radio" value="create" style={{height: 'auto'}}>
          <div className="editor-fields-radio-icon">
            <PlusOutlined />
          </div>
          {intl.formatMessage({id: 'createElement'})}
        </Radio.Button>
        <Radio.Button className="editor-fields-radio" value="edit" style={{height: 'auto'}}>
          <div className="editor-fields-radio-icon">
            <FullscreenOutlined />
          </div>
          {intl.formatMessage({id: 'editElement'})}
        </Radio.Button>
        <Radio.Button className="editor-fields-radio" value="delete" style={{height: 'auto'}}>
          <div className="editor-fields-radio-icon">
            <CloseOutlined />
          </div>
          {intl.formatMessage({id: 'deleteElement'})}
        </Radio.Button>
      </Radio.Group>

      {props.currentMode ? <div></div> : null}

      {props.currentMode === 'create' ? (
        <div>
          {/* <div className="editor-fields">
            <div className="editor-fields-name">{intl.formatMessage({id: 'enterObjectName'})}:</div>
            <Input
              value={props.currentElementName}
              onChange={(e) => props.onCurrentElementNameChange(e.target.value)}
            />
          </div> */}
          <div className="editor-fields">
            <div className="editor-fields-name">
              {intl.formatMessage({id: 'chooseObjectType'})}:
            </div>
            <Select
              value={props.selectedElementType}
              style={{width: '100%', marginBottom: '10px'}}
              onChange={props.onElementTypeSelected}
              options={props.availableElementTypes.map((elementTypeId) => ({
                value: elementTypeId,
                label: intl.formatMessage({
                  id: elementTypeService.getElementType(elementTypeId).displayName,
                }),
              }))}
            />
          </div>
          <div className="editor-fields-name">
            {intl.formatMessage({id: 'selectTemplateOrDrawingTool'})}:
          </div>
          <Tabs
            onChange={() => null}
            type="card"
            items={[
              showTemplates && {
                key: '1',
                label: intl.formatMessage({id: 'createFromTemplate'}),
                children: (
                  <TemplatesGallery
                    templates={templates}
                    setDraggableElement={props.onDraggableElementChange}
                    setCurrentElementName={props.onCurrentElementNameChange}
                  />
                ),
              },
              showShapes && {
                key: '2',
                label: intl.formatMessage({id: 'draw'}),
                children: (
                  <div className="editor-fields">
                    <div className="editor-fields-name">
                      {intl.formatMessage({id: 'chooseObjectForm'})}:
                    </div>
                    <Radio.Group
                      value={props.tool}
                      onChange={(e) => props.onToolSelected(e.target.value)}
                      style={{wordWrap: 'break-word', display: 'flex'}}
                    >
                      {shapes
                        .filter((shape) => availableShapeTypesSet.has(shape.value))
                        .map((shape) => (
                          <Radio.Button
                            key={shape.value}
                            className="editor-fields-radio"
                            value={shape.value}
                            style={{height: 'auto', overflowWrap: 'anywhere'}}
                          >
                            <div className="editor-fields-radio-icon">{shape.icon}</div>
                            {intl.formatMessage({id: shape.value})}
                          </Radio.Button>
                        ))}
                    </Radio.Group>
                    {props.tool ? (
                      <div>{intl.formatMessage({id: 'putObjectOnThePlan'})}</div>
                    ) : null}
                  </div>
                ),
              },
            ].filter(Boolean)}
          />
        </div>
      ) : null}

      {props.currentMode === 'edit' ? (
        <div>
          {!props.currentElement ? (
            <div className="editor-fields-name">
              {intl.formatMessage({id: 'selectAnObjectForEditing'})}
            </div>
          ) : !props.availableElementTypes.some((type) => type === props.currentElement.type) ? (
            <div className="editor-fields-name">
              {intl.formatMessage({id: 'editedInAnotherStep'})}
            </div>
          ) : (
            <div>
              <div className="editor-fields">
                <div className="editor-fields-name">{intl.formatMessage({id: 'objectName'})}:</div>
                <Input
                  value={props.currentElement.name}
                  onChange={(evt) => {
                    props.onCurrentElementUpdated({...props.currentElement, name: evt.target.value})
                  }}
                />
              </div>
              <div className="editor-fields">
                <div className="editor-fields-name">{intl.formatMessage({id: 'objectType'})}:</div>
                <Select
                  value={props.currentElement.type}
                  style={{width: 120}}
                  onChange={(newType: ElementTypeId) => {
                    props.onCurrentElementUpdated({...props.currentElement, type: newType})
                  }}
                  options={props.availableElementTypes.map((elementTypeId) => ({
                    value: elementTypeId,
                    label: intl.formatMessage({
                      id: elementTypeService.getElementType(elementTypeId).displayName,
                    }),
                  }))}
                />
              </div>
              <div className="editor-fields">
                <div className="editor-fields-name">
                  {intl.formatMessage({id: 'objectParameters'})}:
                </div>
                <div className="editor-fields-value">
                  {props.currentElement && (
                    <TransformerUIManager
                      element={props.currentElement}
                      onChange={props.onCurrentElementUpdated}
                      transformerProps={props.transformerProps}
                      onTransformerPropsChange={props.onTransformerPropsChange}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {props.currentMode === 'delete' ? (
        <div>
          {!props.currentElement ? (
            <div className="editor-fields-name">
              {intl.formatMessage({id: 'selectAnElementForDeleting'})}
            </div>
          ) : !props.availableElementTypes.some((type) => type === props.currentElement.type) ? (
            <div className="editor-fields-name">
              {intl.formatMessage({id: 'editedInAnotherStep'})}
            </div>
          ) : (
            <div>
              <div className="editor-fields-name">
                {intl.formatMessage({id: 'doYouReallyWantToDelete'})}{' '}
                {intl.formatMessage({id: props.currentElement.type})} {props.currentElement.name}?
              </div>
              <Radio.Group
                className="editor-fields"
                value={''}
                onChange={(e) => {
                  if (e.target.value === 'yes') {
                    // props.elementsList.splice(
                    //   props.elementsList.findIndex(
                    //     (item) => item.elementId === props.currentElement.elementId
                    //   ),
                    //   1
                    // )
                    // submit({method: 'post'})
                    props.onCurrentElementDeleted(props.currentElement.elementId)
                  } else if (e.target.value === 'no') {
                    props.onCurrentModeChange('edit')
                  }
                }}
              >
                <Radio.Button value={'yes'}>{intl.formatMessage({id: 'yes'})}</Radio.Button>
                <Radio.Button value={'no'}>{intl.formatMessage({id: 'no'})}</Radio.Button>
              </Radio.Group>
            </div>
          )}
        </div>
      ) : null}

      {props.isUnsaved ? (
        <div className="editor-fields-warning">
          {intl.formatMessage({id: 'projectChangedSaveBeforeProceedingToTheNextStep'})}
        </div>
      ) : null}

      <Button style={{margin: '20px 0', width: '100%'}} type="primary" onClick={props.onSave}>
        {intl.formatMessage({id: 'save'})}
      </Button>
    </div>
  )
}
